/* eslint-disable max-lines */
import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import cx from 'classnames';
import {
    renderProp,
    omitProps,
    pickProps,
    mergeProps,
    deriveStateFromWatchedProps,
    isElementOfComponent,
} from 'utils/react';
import { mergeRefs } from 'utilsTS/react';
import { eventHasKey, KEY } from 'utils/dom';
import { deprecate } from 'utils/error';
import { generateId } from 'utils/string';
import { createEnum } from 'utils/enum';
import { NewDropdown as Dropdown } from '../NewDropdown';

import { itemPropTypes } from '../propTypes';
import { sectionsOrItemsPropType } from './itemPropTypes';
import { MenuDropdownSpinner, MenuDropdownEmptyItem, MenuDropdownItem } from './components';
import { isSection, findItemByValue } from './helpers';
import styles from './MenuDropdown.module.scss';

export const MENUDROPDOWN_ALIGNMENT = createEnum({
    LEFT: 'bottom-start',
    CENTER: 'bottom',
    RIGHT: 'bottom-end',
    TOGGLE: 'toggle',
});

export default class MenuDropdown extends Component {
    static displayName = 'MenuDropdown';

    static ALIGNMENT = MENUDROPDOWN_ALIGNMENT;

    static propTypes = {
        ...Dropdown.propTypes,
        alignment: PropTypes.oneOf(Object.values(MENUDROPDOWN_ALIGNMENT)),
        onFocus: PropTypes.func,
        onClose: PropTypes.func,
        onClick: PropTypes.func,
        onKeyDown: PropTypes.func,
        onBeforeOpen: PropTypes.func,
        onBeforeClose: PropTypes.func,
        padded: PropTypes.bool,
        toggleOnClick: PropTypes.bool,
        toggleWithSpace: PropTypes.bool,
        toggleWithEnter: PropTypes.bool,
        items: sectionsOrItemsPropType.isRequired,
        highlightedItem: PropTypes.shape(itemPropTypes),
        emptyLabel: PropTypes.node,
        isLoading: PropTypes.bool,
        loadingText: PropTypes.string,
        selectWithSpace: PropTypes.bool,
        // DEPRECATED
        highlightWithArrow: PropTypes.bool,
        ignoreNavigationKeys: PropTypes.bool,
        openWithArrow: PropTypes.bool,
        closeOnItemClick: PropTypes.bool,
        toggleRef: PropTypes.ref(),
        popoverRef: PropTypes.ref(),
        isItemSelected: PropTypes.func,
        isMenu: PropTypes.bool,
        itemComponent: PropTypes.func,
        itemComponentProps: PropTypes.func,
        renderItem: PropTypes.func,
        renderItemsContainer: PropTypes.func,
        onSelect: PropTypes.func,
        onHighlight: PropTypes.func,
        menuClassName: PropTypes.string,
        dropdownClassName: PropTypes.string,
        style: PropTypes.shape({}),
    };

    static defaultProps = {
        alignment: MENUDROPDOWN_ALIGNMENT.RIGHT,
        onFocus: noop,
        onClose: noop,
        onClick: noop,
        onKeyDown: noop,
        onBeforeOpen: noop,
        onBeforeClose: noop,
        padded: false,
        toggleOnClick: true,
        toggleWithSpace: true,
        toggleWithEnter: true,
        highlightedItem: undefined,
        emptyLabel: null,
        isLoading: false,
        loadingText: null,
        selectWithSpace: true,
        highlightWithArrow: true,
        ignoreNavigationKeys: false,
        openWithArrow: true,
        closeOnItemClick: true,
        toggleRef: noop,
        popoverRef: noop,
        isItemSelected: null,
        isMenu: true,
        itemComponent: MenuDropdownItem,
        itemComponentProps: noop,
        renderItem: noop,
        renderItemsContainer: noop,
        onSelect: noop,
        onHighlight: noop,
        menuClassName: null,
        dropdownClassName: null,
        style: undefined,
    };

    static getDerivedStateFromProps(props, state) {
        return deriveStateFromWatchedProps(props, state, {
            id: () => props.id || generateId('menu-dropdown'),
            highlightedItem: () => {
                if (props.highlightedItem !== undefined) {
                    return props.highlightedItem;
                }
                return state.highlightedItem || null;
            },
            isOpen: () => props.isOpen,
        });
    }

    state = {
        id: null,
        highlightedItem: null,
        isOpen: undefined,
    };

    containerRef = createRef();

    dropdownRef = createRef();

    toggleRef = createRef();

    items = [];

    componentDidMount() {
        const { highlightWithArrow } = this.props;
        deprecate(
            highlightWithArrow === false,
            'highlightWithArrow',
            'For a11y reasons, please do not use it.',
        );

        this.ensureHighlightedItemIsVisible();
    }

    componentDidUpdate(previousProps, previousState) {
        const { highlightedItem } = this.state;
        const flatItems = this.getFlatItemsWithoutLoading();

        if (highlightedItem !== previousState.highlightedItem) {
            this.ensureHighlightedItemIsVisible();
        }

        if (highlightedItem && !flatItems.includes(highlightedItem)) {
            const newHighlightedItem = findItemByValue(flatItems, highlightedItem);
            if (newHighlightedItem && highlightedItem !== newHighlightedItem) {
                this.setState({ highlightedItem: newHighlightedItem });
            }
        }
    }

    getFlatItemsWithoutLoading() {
        const { items } = this.props;

        // filter loading sections out
        const itemsWithoutLoading = items.filter((item) => !isSection(item) || !item.isLoading);

        return itemsWithoutLoading.reduce((previous, item) => {
            if (isSection(item)) {
                return [...previous, ...item.items];
            }

            return [...previous, item];
        }, []);
    }

    getNextItem({ reverse = false, first = false }) {
        const { highlightedItem } = this.state;

        const flatItems = this.getFlatItemsWithoutLoading();

        const itemsDir = reverse ? [...flatItems].reverse() : flatItems;
        const startAtIndex = first
            ? 0
            : itemsDir.findIndex((item) => item.value === highlightedItem?.value) + 1;
        const items = [...itemsDir.slice(startAtIndex), ...itemsDir];

        const nextItem = items.find((item) => !item.disabled);
        return nextItem || null;
    }

    getFirstItem() {
        const flatItems = this.getFlatItemsWithoutLoading();
        return flatItems.find((item) => !item.disabled);
    }

    shouldCloseOnSelect(item) {
        const { closeOnItemClick } = this.props;
        const isOrdinaryItem = !item.control;

        return closeOnItemClick && isOrdinaryItem;
    }

    handleItemClick = (event, item, index) => {
        event.stopPropagation();
        const isClick = event.type === 'click';

        if (item.onClick) {
            item.onClick(event);
        } else if (item.href && !isClick) {
            this.items[index].click();
        }

        this.handleItemSelect(item, index);

        if (isClick && this.shouldCloseOnSelect(item)) {
            this.closeDropdown();
        }
    };

    handleItemSelect = (item, index) => {
        const { onSelect } = this.props;

        onSelect(item, index);
    };

    handleKeyDown = (event) => {
        const { ENTER, SPACE, HOME, END, UP, DOWN } = KEY;
        const { onKeyDown, selectWithSpace } = this.props;
        const shouldHandleSpace = selectWithSpace && eventHasKey(event, SPACE);

        if (eventHasKey(event, HOME, END, UP, DOWN)) {
            this.handleKeyDownNav(event);
        } else if (eventHasKey(event, ENTER) || shouldHandleSpace) {
            this.handleKeyDownEnterAndSpace(event);
        }

        onKeyDown(event);
    };

    handleDropdownClick = (event) => {
        const { onClick } = this.props;

        onClick(event);
    };

    handleKeyDownNav = (event) => {
        const { HOME, END, UP, DOWN } = KEY;
        const { highlightWithArrow, ignoreNavigationKeys, openWithArrow } = this.props;
        const { isOpen } = this.state;
        const { current: dropdown } = this.dropdownRef;

        if (!highlightWithArrow) return;
        if (eventHasKey(event, HOME, END) && (ignoreNavigationKeys || !isOpen)) return;
        if (eventHasKey(event, UP, DOWN) && !openWithArrow && !isOpen) return;

        event.preventDefault();

        if (!isOpen) {
            dropdown.open();
        }

        const reverse = eventHasKey(event, UP, END);
        const first = eventHasKey(event, HOME, END);
        const nextItem = this.getNextItem({ reverse, first });

        this.highlightItem(nextItem);
    };

    handleKeyDownEnterAndSpace = (event) => {
        const { ENTER, SPACE } = KEY;
        const { toggleWithEnter, toggleWithSpace } = this.props;
        const { highlightedItem, isOpen } = this.state;

        const isSpace = eventHasKey(event, SPACE) && toggleWithSpace;
        const isEnter = eventHasKey(event, ENTER) && toggleWithEnter;

        if (isOpen && highlightedItem) {
            const flatItems = this.getFlatItemsWithoutLoading();

            event.preventDefault();
            this.handleItemClick(event, highlightedItem, flatItems.indexOf(highlightedItem));

            if (this.shouldCloseOnSelect(highlightedItem) && (isSpace || isEnter)) {
                this.closeDropdown();
            }
        }

        if (!isOpen) {
            const firstItem = this.getFirstItem();
            this.highlightItem(firstItem);
        }
    };

    highlightItem = (highlightedItem) => {
        const { items, onHighlight } = this.props;

        if (!highlightedItem) return;

        this.setState({ highlightedItem });

        const flatItems = items.flatMap((item) => item?.items || item);
        const index = flatItems.indexOf(highlightedItem);
        this.items[index]?.focus();

        if (onHighlight) {
            onHighlight(highlightedItem, index);
        }
    };

    handleBeforeOpen = () => {
        const { onBeforeOpen } = this.props;
        this.ensureHighlightedItemIsVisible();
        this.setState({ isOpen: true });
        onBeforeOpen();
    };

    handleBeforeClose = () => {
        const { onBeforeClose } = this.props;
        this.setState({ isOpen: false });
        onBeforeClose();
    };

    closeDropdown = () => {
        const { current: dropdown } = this.dropdownRef;
        const { current: toggle } = this.toggleRef;
        dropdown.close();
        toggle?.focus();
    };

    ensureHighlightedItemIsVisible = () => {
        const { current: container } = this.containerRef;
        const { highlightedItem } = this.state;

        if (!highlightedItem || !container) return;

        const highlightedIndex = this.getFlatItemsWithoutLoading().indexOf(highlightedItem);
        const itemElement = this.items[highlightedIndex];

        if (!itemElement) return;

        const element = itemElement.closest('li') || itemElement;
        const parentOffset = element.offsetParent?.offsetTop || 0;
        const elementOffset = element.offsetTop + parentOffset - container.offsetTop;

        let { scrollTop } = container; // Top of the visible area
        if (elementOffset < scrollTop) {
            // Item is off the top of the visible area
            scrollTop = elementOffset;
        } else if (elementOffset + element.offsetHeight > scrollTop + container.offsetHeight) {
            // Item is off the bottom of the visible area
            scrollTop = elementOffset + (element.offsetHeight - container.offsetHeight);
        }

        if (scrollTop !== container.scrollTop) {
            container.scrollTop = scrollTop;
        }
    };

    renderItem = (item, index) => {
        const {
            itemComponent: ItemComponent,
            itemComponentProps,
            isItemSelected,
            isMenu,
            renderItem,
        } = this.props;
        const { id, highlightedItem } = this.state;

        const dropdownProps = {
            ...pickProps(item, ItemComponent),
        };

        const isMenuItem = isElementOfComponent(ItemComponent, MenuDropdownItem)
            ? isMenu
            : undefined;

        return (
            <ItemComponent
                {...dropdownProps}
                key={`item-${index}`}
                innerRef={(c) => {
                    this.items[index] = c;
                }}
                highlight={item === highlightedItem}
                selected={isItemSelected && isItemSelected(item)}
                id={`${id}-item-${index}`}
                index={index}
                {...itemComponentProps(item)}
                {...item}
                isMenuItem={isMenuItem}
                onClick={(event) => this.handleItemClick(event, item, index)}
                onKeyDown={(event) => this.handleKeyDown(event)}
            >
                {renderProp(renderItem, item.label, { item })}
            </ItemComponent>
        );
    };

    renderItems = () => {
        const { items, emptyLabel, isMenu, renderItemsContainer } = this.props;

        if (emptyLabel && items.length === 0) {
            return <MenuDropdownEmptyItem isMenuItem={isMenu}>{emptyLabel}</MenuDropdownEmptyItem>;
        }

        const flatItems = this.getFlatItemsWithoutLoading();

        const children = items.map((item, index) => {
            if (isSection(item)) {
                return this.renderSection(item, index, flatItems);
            }

            return this.renderItem(item, flatItems.indexOf(item));
        });

        return renderProp(renderItemsContainer, children, { children });
    };

    renderSection = (section, sectionIndex, flatItems) => {
        const { isMenu } = this.props;
        const {
            section: sectionLabel,
            isLoading,
            loadingText,
            emptyLabel,
            items: sectionItems,
        } = section;
        const key = `section-${sectionIndex}`;

        const hasItems = sectionItems.length > 0;

        if (!isLoading && !hasItems && !emptyLabel) return null;

        const renderedItems = sectionItems.map((item) =>
            this.renderItem(item, flatItems.indexOf(item)),
        );

        return (
            <li key={key} role="none" className={styles.menuItem}>
                <span className={styles.section}>{sectionLabel}</span>
                <ul className={styles.menu}>
                    <MenuDropdownSpinner
                        isLoading={isLoading}
                        loadingText={loadingText}
                        className={styles.menuItem}
                    >
                        {hasItems ? (
                            renderedItems
                        ) : (
                            <MenuDropdownEmptyItem isMenuItem={isMenu}>
                                {emptyLabel}
                            </MenuDropdownEmptyItem>
                        )}
                    </MenuDropdownSpinner>
                </ul>
            </li>
        );
    };

    render() {
        const {
            isLoading,
            isMenu,
            loadingText,
            toggleRef: toggleRefProp,
            dropdownRef: dropdownRefProp,
            maxHeight,
            className,
            menuClassName,
            dropdownClassName,
            style,
        } = this.props;
        const { id } = this.state;

        this.items = [];

        const menuClassNames = cx('dropdown-menu', styles.menu, menuClassName);
        const menuStyle = mergeProps(style, maxHeight && { maxHeight });
        const items = this.renderItems();

        return (
            <Dropdown
                {...pickProps(this.props, Dropdown)}
                useNewAria
                padded={false}
                scrollable={false}
                dropdownRef={mergeRefs(this.dropdownRef, dropdownRefProp)}
                toggleRef={mergeRefs(this.toggleRef, toggleRefProp)}
                className={className}
                dropdownClassName={dropdownClassName}
                onBeforeOpen={this.handleBeforeOpen}
                onBeforeClose={this.handleBeforeClose}
                onKeyDown={this.handleKeyDown}
                onClick={this.handleDropdownClick}
            >
                <ul
                    {...omitProps(this.props, Dropdown, MenuDropdown)}
                    ref={this.containerRef}
                    id={`${id}-menu`}
                    aria-labelledby={id}
                    role={isMenu ? 'menu' : 'listbox'}
                    tabIndex={-1}
                    style={menuStyle}
                    className={menuClassNames}
                >
                    <MenuDropdownSpinner
                        isLoading={isLoading}
                        loadingText={loadingText}
                        className={styles.menuItem}
                    >
                        {items}
                    </MenuDropdownSpinner>
                </ul>
            </Dropdown>
        );
    }
}
